import * as React from 'react';
import {CircularProgressbarWithChildren} from 'react-circular-progressbar';
import {Transition} from 'react-transition-group';
import {useSelector, useDispatch} from 'react-redux';
import Immutable from 'immutable';
import {useHistory, useLocation, Redirect} from 'react-router-dom';
import {useLingui} from '@lingui/react';
import {Trans} from '@lingui/macro';

import TestContainer from '@containers/TestContainer';
import WelcomePage from './WelcomePage.js/WelcomePage';
import SectionsPage from './SectionsPage/SectionsPage';
import Logo from './FerrariIcon/Logo';
import PlayVid from './FerrariIcon/PlayVid';
import Documents from './FerrariIcon/Documents';
import Awareness from './FerrariIcon/Awareness';
import DigitalProgress from './FerrariIcon/DigitalProgress';
import Knowledge from './FerrariIcon/Knowledge';
import Personal from './FerrariIcon/Personal';
import Escape from './FerrariIcon/Escape';
import LoadingSpinner from '@components/LoadingSpinner';
import CompletedPage from './CompletedPage.js/CompletedPage';
import FerrariTutorial from './FerrariTutorial/FerrariTutorial';
import DocumentsPage from './FerrariDocuments/FerrariDocuments';

import {loadTests} from '@actions/tests';
import {logout} from '@actions/login';

import {HOME, TEST} from '@services/routes';

import cl from 'classnames';
import s from './FerrariTheme.module.css';
import 'react-circular-progressbar/dist/styles.css';
import 'driver.js/dist/driver.min.css';
import {i18n} from '@services/i18n';

export default function FerrariTheme() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const tests = useSelector(state => state.getIn(['tests', 'list']));
  const currentTestData = useSelector(state => state.getIn(['tests', 'current']));
  const user = useSelector(state => state.getIn(['user', 'data']));

  function goHome() {
    history.push(HOME);
  }

  React.useEffect(() => {
    dispatch(loadTests());
  }, []);

  const firstName = user.get('firstName');
  const lastName = user.get('lastName');

  if (location.pathname === TEST) {
    if (currentTestData.size === 0) {
      return <Redirect to={HOME} />;
    }
    return (
      <div className={s.FerrariThemeContainer}>
        <header className={s.FerrariHeader}>
          <div className={s.FerrariUserInfo}>
            <p className={s.FerrariUsername}>
              {firstName} {lastName}
            </p>
          </div>
          <Logo />
          <div className={cl(s.Leave, s.Test)}>
            <button className={s.CloseTest} onClick={goHome}>
              X
            </button>
          </div>
        </header>
        <div className={s.TestWrapper}>
          <TestContainer props={currentTestData.toJS()} className={s.Full} />
        </div>
      </div>
    );
  }
  if (tests.size === 0) {
    return <LoadingSpinner />;
  }
  return <FerrariThemeWithData tests={tests} firstName={firstName} lastName={lastName} />;
}

function FerrariThemeWithData(props) {
  const dispatch = useDispatch();
  const {i18n} = useLingui();
  const {tests, firstName, lastName} = props;

  const campaignData = useSelector(state => {
    return state.getIn(['userCampaign', 'data', 'data']);
  });
  const campaign = useSelector(state => state.getIn(['userCampaign', 'data']));
  const campaignLanguage = campaign.get('lang');

  const [sections, setSections] = React.useState(() => {
    const order = campaignData.get('order');
    const returnCampaignData = campaignData.withMutations(map => {
      const sectionsData = map.getIn(['sections']);

      order.forEach(sectionName => {
        let newTests = Immutable.List();
        sectionsData.getIn([sectionName, 'tests']).forEach(test => {
          const testId = test.get('id');
          const foundTest = tests.find(fullTest => {
            return fullTest.get('id') === testId;
          });
          newTests = newTests.push(test.merge(foundTest));
        });
        map.setIn(['sections', sectionName, 'tests'], newTests);
      });
    });
    return returnCampaignData.get('sections').toJS();
  });

  const [hasClickedButton, setHasClickedButton] = React.useState(false);
  const [hasFinishedAnimating, setHasFinishedAnimating] = React.useState(false);
  const [showTutorial, setShowTutorial] = React.useState(false);
  const [rewatchTutorial, setRewatchTutorial] = React.useState(false);

  const [showDocs, setShowDocs] = React.useState(false);

  function changePage() {
    setHasClickedButton(prevClick => !prevClick);
  }

  function resetTutorial() {
    setRewatchTutorial(false);
    setShowTutorial(false);
  }

  function exitDocs() {
    setShowDocs(false);
  }

  const order = campaignData.get('order');

  // No longer returns array, only first instance of incomplete or not attempted
  const currentSectionSelected = order.find(sectionName =>
    sections[sectionName].tests.find(
      test => test.status === 'incomplete' || test.status === 'not attempted'
    )
  );

  const currentSection = currentSectionSelected || order.getIn([3]);

  // Amount of tests completed for the current section
  const testCompletion = sections[currentSection].tests.filter(
    test => test.status === 'complete'
  ).length;

  // Total number of tests in the current section
  const sectionTests = sections[currentSection].tests.length;

  const sectionMotivation = sections[currentSection].motivation;

  // Number of sections completed / Total number of sections
  const sectionsCompletedProgress =
    (order.filter(sectionName =>
      sections[sectionName].tests.every(test => test.status === 'complete')
    ).size /
      order.size) *
    100;

  const campaignIntro = campaignData.getIn(['intro']);
  const campaignOutro = campaignData.getIn(['outro']);

  // uses order array to get sections in correct order
  const isKnowledgeComplete = sections[order.getIn([0])].tests.every(
    test => test.status === 'complete'
  );
  const isDigitalComplete = sections[order.getIn([1])].tests.every(
    test => test.status === 'complete'
  );
  const isAwarenessCompelete = sections[order.getIn([2])].tests.every(
    test => test.status === 'complete'
  );
  const isPersonalCompelete = sections[order.getIn([3])].tests.every(
    test => test.status === 'complete'
  );

  if (showDocs) {
    return (
      <div className={s.FerrariThemeContainer}>
        <header className={s.FerrariHeader}>
          <div className={s.FerrariUserInfo}>
            <p className={s.FerrariUsername}>
              {firstName} {lastName}
            </p>
          </div>
          <Logo />
          <div className={s.Leave} onClick={() => dispatch(logout())}>
            <Trans id="ferrari.exit">Exit</Trans>
            <Escape />
          </div>
        </header>

        <DocumentsPage data={campaignData} leavedocs={exitDocs} />
      </div>
    );
  } else {
    return (
      <div className={s.FerrariThemeContainer}>
        <header className={s.FerrariHeader}>
          <div className={s.FerrariUserInfo}>
            <p className={s.FerrariUsername}>
              {firstName} {lastName}
            </p>
          </div>
          <Logo />
          <div className={s.Leave} onClick={() => dispatch(logout())}>
            <Trans id="ferrari.exit">Exit</Trans>
            <Escape />
          </div>
        </header>

        <div id="ferrari-sub-header" className={s.FerrariSubHeader}>
          <div className={s.FerrariActivity}>
            <div className={s.ActivityInfo}>
              <p className={s.ActivityName}> Individual Challenge</p>
            </div>
            <div id="ferrari-sub-header-progress" className={s.ActivityProg}>
              <p className={s.ActivityMsg}>{sectionMotivation}</p>
              <div className={s.ActivityProgressWrapper}>
                <CircularProgressbarWithChildren
                  value={sectionsCompletedProgress}
                  text={`${sectionsCompletedProgress}%`}
                  styles={{
                    // Customize the root svg element
                    root: {},
                    // Customize the path, i.e. the "completed progress"
                    path: {
                      // Path color
                      filter: 'blur(1px)',
                      stroke: '#cd212a',
                      // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
                      strokeLinecap: 'square',
                      // Customize transition animation
                      transition: 'stroke-dashoffset 0.5s ease 0s',
                      // Rotate the path

                      transformOrigin: 'center center',
                    },
                    // Customize the circle behind the path, i.e. the "total progress"
                    trail: {
                      // Trail color
                      stroke: '#747474',
                      strokeWidth: '5px',
                      // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
                      strokeLinecap: 'butt',
                      // Rotate the trail
                      transform: 'rotate(0.25turn)',
                      transformOrigin: 'center center',
                    },
                    text: {
                      // Text color
                      fill: '#FFFFFF',
                      // Text size
                      fontSize: '22px',
                    },
                  }}
                ></CircularProgressbarWithChildren>
              </div>

              <div className={s.IconProgress}>
                <Knowledge
                  color={isKnowledgeComplete ? '#fff200' : '#707070'}
                  className={s.IconProgressItem}
                ></Knowledge>
                <span
                  style={isKnowledgeComplete ? {background: '#fff200'} : {background: '#707070'}}
                  className={s.HorizontalBar}
                />
                <DigitalProgress
                  color={isDigitalComplete ? '#fff200' : '#707070'}
                  className={s.IconProgressItem}
                ></DigitalProgress>
                <span
                  style={isDigitalComplete ? {background: '#fff200'} : {background: '#707070'}}
                  className={s.HorizontalBar}
                />
                <Awareness
                  color={isAwarenessCompelete ? '#fff200' : '#707070'}
                  className={s.IconProgressItem}
                ></Awareness>
                <span
                  style={isAwarenessCompelete ? {background: '#fff200'} : {background: '#707070'}}
                  className={s.HorizontalBar}
                />
                <Personal
                  color={isPersonalCompelete ? '#fff200' : '#707070'}
                  className={s.IconProgressItem}
                ></Personal>
              </div>
            </div>
          </div>

          {showTutorial || rewatchTutorial ? (
            <FerrariTutorial resetTutorial={resetTutorial} />
          ) : null}
          <div id="ferrari-sub-header-buttons" className={s.AdditionalInfo}>
            <button
              id="ferrari-review-docs"
              className={s.InfoButtons}
              onClick={() => setShowDocs(!showDocs)}
            >
              {i18n._('ferrari.documents')}
              <Documents className={s.InfoIcon} />
            </button>
            <button
              id="ferrari-rewatch"
              className={s.InfoButtons}
              onClick={() => setRewatchTutorial(!rewatchTutorial)}
            >
              {i18n._('ferrari.rewatch_tut')}
              <PlayVid className={s.InfoIcon} />
            </button>
          </div>
        </div>
        {/* Need to keep track of progress, so state that determines if all tests not attempted (render welcome like we have now)
      and condition for all tests finsihed which will render the completion page (all tests completed) */}
        {sectionsCompletedProgress === 100 ? (
          <CompletedPage
            testsCompleted={testCompletion}
            testsTotal={sectionTests}
            sections={sections}
            order={order}
            percent={sectionsCompletedProgress}
            current={currentSection}
            outro={campaignOutro}
            user={firstName}
            changePage={changePage}
          />
        ) : testCompletion === 0 && currentSection === order.toJS()[0] ? (
          !hasFinishedAnimating ? (
            <Transition
              in={!hasClickedButton}
              timeout={500}
              onExited={() => setHasFinishedAnimating(true)}
            >
              {state => (
                <WelcomePage
                  animationState={state}
                  testsCompleted={testCompletion}
                  testsTotal={sectionTests}
                  sections={sections}
                  order={order}
                  percent={sectionsCompletedProgress}
                  current={currentSection}
                  intro={campaignIntro}
                  user={firstName}
                  changePage={changePage}
                />
              )}
            </Transition>
          ) : (
            <Transition in={hasClickedButton} timeout={300} onEntered={() => setShowTutorial(true)}>
              {state => (
                <SectionsPage
                  animationState={state}
                  testsCompleted={testCompletion}
                  testsTotal={sectionTests}
                  sections={sections}
                  order={order}
                  percent={sectionsCompletedProgress}
                  currentSection={currentSection}
                  lang={campaignLanguage}
                />
              )}
            </Transition>
          )
        ) : (
          <SectionsPage
            testsCompleted={testCompletion}
            testsTotal={sectionTests}
            sections={sections}
            order={order}
            percent={sectionsCompletedProgress}
            currentSection={currentSection}
            lang={campaignLanguage}
          />
        )}
      </div>
    );
  }
}
